/**
 * Which tooltip the bar and the effects rows show, and why (P2.2-T12, ADR-061's amendment of 2026-10-02): one at a
 * time on the page, the newest live trigger's. A trigger is a mouse or pen hover held `TIP_HOVER_MS`, keyboard focus,
 * or a pin: a press held `LONG_PRESS_MS` (by touch, pen or mouse alike, its click swallowed so a long press never uses
 * a slot), or a click on an effect icon, pins the tooltip open after the release until a press elsewhere, its close
 * button, Escape, or a second click on the pinned icon (the author's ruling of 2026-10-02, the design's rule 24). Ending
 * a trigger hides its tooltip and shows the one an older live trigger holds, if any. The view hands the controller the
 * events; it holds no DOM and runs on any timer, so it is tested on hand-run ones.
 */

/**
 * How long a mouse or pen rests on a slot or an icon before its tooltip shows, in ms (a draft knob).
 */
export const TIP_HOVER_MS = 300;

/**
 * How long a press is held before it is a long press, in ms (P1.9-T51, the author's ruling of 2026-09-26).
 */
export const LONG_PRESS_MS = 500;

/**
 * How long after a long press's release or cancel its click may still arrive and be swallowed, in ms: the click a
 * release makes comes at once, so a swallow left with no click (the press ended off its anchor, or the browser made
 * none) lapses before a later key's or pointer's click could meet it.
 */
export const SWALLOW_MS = 100;

/**
 * What a tooltip is for: an ability slot by its index, an icon of the player's effects row or its target's by its
 * place in the row, the XP bar (index 0, P2.4-T8), the stance button (index 0, P2.2-T13), or a slot of the Inventory
 * window by its bag slot (P2.8-T11), a mark of the wallet line by its place in the line (P2.8-T67, `WALLET_MARKS`), or
 * the Inventory window footer's carry number (index 0, P2.8-T53), the player card's name (index 0, P2.8-T68), or a control carrying words of its own (`HoverWords`, the wrapper's number, P2.8-T68).
 */
export interface TipAnchor {
  readonly surface: "slot" | "own" | "target" | "xp" | "stance" | "bag" | "wallet" | "carry" | "name" | "words";
  readonly index: number;
}

/**
 * What can hold a tooltip open.
 */
type TipTrigger = "hover" | "focus" | "pin";

/**
 * The tooltip shown: its anchor, and whether it is pinned, so it draws its close button.
 */
export interface TipShown {
  readonly anchor: TipAnchor;
  readonly pinned: boolean;
}

/**
 * Runs `run` once after `ms`, returning a way to cancel it.
 */
export type TipTimer = (run: () => void, ms: number) => () => void;

/**
 * The page's timer, on `setTimeout`.
 *
 * @param run What to run.
 * @param ms After how long.
 * @returns Cancels it.
 */
const PAGE_TIMER: TipTimer = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/**
 * Reads an anchor from the `data-tip` attribute the views write (`slot:3`, `own:0`, `target:1`, `xp:0`, `stance:0`, `bag:4`, `wallet:2`, `carry:0`, `name:0`, `words:3`).
 *
 * @param value The attribute, or null.
 * @returns The anchor, or null for none or a malformed one.
 * @example
 * parseTipAnchor("slot:3");
 * // { surface: "slot", index: 3 }
 */
export function parseTipAnchor(value: string | null): TipAnchor | null {
  const match = value === null ? null : /^(slot|own|target|xp|stance|bag|wallet|carry|name|words):(\d+)$/.exec(value);
  if (match === null) {
    return null;
  }
  return { surface: match[1] as TipAnchor["surface"], index: Number(match[2]) };
}

/**
 * Whether two anchors name the same slot or icon; two nulls are the same.
 *
 * @param a One anchor.
 * @param b The other.
 * @returns True when they match.
 */
export function sameAnchor(a: TipAnchor | null, b: TipAnchor | null): boolean {
  return a === b || (a !== null && b !== null && a.surface === b.surface && a.index === b.index);
}

/**
 * The controller.
 *
 * @example
 * ```ts
 * const tips = new TipController();
 * tips.hoverIn({ surface: "slot", index: 1 });
 * ```
 */
export class TipController {
  private live: { readonly trigger: TipTrigger; readonly anchor: TipAnchor }[] = [];
  private shown: TipShown | null = null;
  private readonly listeners = new Set<() => void>();
  private cancelHover: (() => void) | null = null;
  private cancelPress: (() => void) | null = null;
  private pressing: TipAnchor | null = null;
  private swallow: TipAnchor | null = null;
  private cancelSwallow: (() => void) | null = null;

  /**
   * @param timer Schedules the hover's delay and the long press; `setTimeout` on the page.
   */
  public constructor(private readonly timer: TipTimer = PAGE_TIMER) {}

  /**
   * Adds a listener told each time the anchor shown changes. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The tooltip shown now, the same object until its anchor or its pin changes. Works unbound, as React calls it.
   *
   * @returns The tooltip, or null for none.
   */
  public readonly getSnapshot = (): TipShown | null => this.shown;

  /**
   * A mouse or pen came to rest over an anchor: its tooltip shows after `TIP_HOVER_MS` unless it leaves first.
   *
   * @param anchor The anchor.
   */
  public hoverIn(anchor: TipAnchor): void {
    this.cancelHover?.();
    this.end("hover");
    this.cancelHover = this.timer(() => {
      this.cancelHover = null;
      this.start("hover", anchor);
    }, TIP_HOVER_MS);
  }

  /**
   * The mouse or pen left an anchor.
   *
   * @param anchor The anchor.
   */
  public hoverOut(anchor: TipAnchor): void {
    this.cancelHover?.();
    this.cancelHover = null;
    this.end("hover", anchor);
  }

  /**
   * An anchor took keyboard focus: its tooltip shows at once.
   *
   * @param anchor The anchor.
   */
  public focusIn(anchor: TipAnchor): void {
    this.end("focus");
    this.start("focus", anchor);
  }

  /**
   * An anchor lost keyboard focus.
   *
   * @param anchor The anchor.
   */
  public focusOut(anchor: TipAnchor): void {
    this.end("focus", anchor);
  }

  /**
   * A press began on an anchor: held `LONG_PRESS_MS`, it pins the tooltip and its click is swallowed.
   *
   * @param anchor The anchor.
   */
  public pressDown(anchor: TipAnchor): void {
    this.pressCancel();
    this.dropSwallow();
    this.pressing = anchor;
    this.cancelPress = this.timer(() => {
      this.cancelPress = null;
      this.swallow = anchor;
      this.end("pin");
      this.start("pin", anchor);
    }, LONG_PRESS_MS);
  }

  /**
   * The press on an anchor was released there: a press shorter than a long press shows nothing; a long press's tooltip
   * stays pinned, and its click, arriving within `SWALLOW_MS`, will be swallowed.
   *
   * @param anchor The anchor.
   */
  public pressUp(anchor: TipAnchor): void {
    if (!sameAnchor(this.pressing, anchor)) {
      this.pressRelease();
      return;
    }
    this.cancelPress?.();
    this.cancelPress = null;
    this.pressing = null;
    this.lapseSwallow();
  }

  /**
   * The pressed pointer was released off its anchor, or the browser cancelled it: the press ends, and a fired long
   * press's swallow lapses `SWALLOW_MS` from now.
   */
  public pressRelease(): void {
    this.pressCancel();
    this.lapseSwallow();
  }

  /**
   * The press moved past the slop while still held: before 500 ms it will never be a long press; after, its tooltip
   * stays pinned and the click its release makes is still swallowed, the swallow lapsing only from the release.
   */
  public pressDrift(): void {
    this.cancelPress?.();
    this.cancelPress = null;
  }

  /**
   * The press left its anchor while still held: one shorter than a long press will show nothing; once a long press has
   * fired, its tooltip stays pinned and its swallow is kept until the pointer's real release (`pressUp` or
   * `pressRelease`), however long it is away, so a long press that wanders off and comes back never casts.
   */
  public pressCancel(): void {
    this.cancelPress?.();
    this.cancelPress = null;
    this.pressing = null;
  }

  /**
   * The pinned tooltip's close button: closes whatever is shown, as Escape does. Bound, so the button holds it as is.
   */
  public readonly close = (): void => {
    this.clear();
  };

  /**
   * A click on an anchor: swallowed when it ends a long press there; otherwise an effect icon's or a bag slot's click
   * pins its tooltip, or unpins it when it is already pinned there (a bag slot's click selects it and shows its details,
   * the author's controls of 2026-10-08), and a bar slot's click is left to use the slot, as the stance button's is to
   * toggle.
   *
   * @param anchor The anchor.
   * @returns True when the click is swallowed and must not act.
   */
  public click(anchor: TipAnchor): boolean {
    if (this.swallowClick(anchor)) {
      return true;
    }
    if (anchor.surface === "slot" || anchor.surface === "stance") {
      return false;
    }
    const pinned = this.live.find((entry) => entry.trigger === "pin");
    this.end("pin");
    if (pinned === undefined || !sameAnchor(pinned.anchor, anchor)) {
      this.start("pin", anchor);
    }
    return false;
  }

  /**
   * Whether a click on an anchor is the one that ends a long press there, which must not act; asking takes the swallow,
   * so the next click on it acts.
   *
   * @param anchor The anchor clicked.
   * @returns True when the click is swallowed.
   */
  public swallowClick(anchor: TipAnchor): boolean {
    const swallowed = this.swallow !== null && sameAnchor(this.swallow, anchor);
    this.dropSwallow();
    return swallowed;
  }

  /**
   * A press began anywhere on the page: one off the pinned slot or icon unpins it, and is then only that (the lead's
   * ruling of 2026-10-02: the page consumes it, so it neither selects nor swings nor uses a slot).
   *
   * @param anchor The anchor it began on, or null for none.
   * @returns True when it unpinned a tooltip, so the page must consume the press.
   */
  public pressAnywhere(anchor: TipAnchor | null): boolean {
    const pinned = this.live.find((entry) => entry.trigger === "pin");
    if (pinned !== undefined && !sameAnchor(pinned.anchor, anchor)) {
      this.end("pin");
      return true;
    }
    return false;
  }

  /**
   * Escape: closes whatever is shown, every trigger with it, until a hover, a focus, a press or a click comes again.
   *
   * @returns True when a tooltip was shown, so the press is taken.
   */
  public escape(): boolean {
    const had = this.shown !== null;
    this.clear();
    return had;
  }

  /**
   * Drops every trigger and pending timer, as when the shown anchor's subject is gone.
   */
  public clear(): void {
    this.cancelHover?.();
    this.cancelHover = null;
    this.cancelPress?.();
    this.cancelPress = null;
    this.pressing = null;
    this.dropSwallow();
    this.live = [];
    this.publish();
  }

  /**
   * Lets a long press's swallow lapse `SWALLOW_MS` from now, if one is held and not already lapsing.
   */
  private lapseSwallow(): void {
    if (this.swallow !== null && this.cancelSwallow === null) {
      this.cancelSwallow = this.timer(() => {
        this.cancelSwallow = null;
        this.swallow = null;
      }, SWALLOW_MS);
    }
  }

  /**
   * Drops any swallow and its lapse.
   */
  private dropSwallow(): void {
    this.cancelSwallow?.();
    this.cancelSwallow = null;
    this.swallow = null;
  }

  /**
   * Starts a trigger on an anchor, the newest live one.
   *
   * @param trigger The trigger.
   * @param anchor The anchor.
   */
  private start(trigger: TipTrigger, anchor: TipAnchor): void {
    this.live = [...this.live.filter((entry) => entry.trigger !== trigger), { trigger, anchor }];
    this.publish();
  }

  /**
   * Ends a trigger, on any anchor or only on the one named.
   *
   * @param trigger The trigger.
   * @param anchor The anchor it must be on, or none for any.
   */
  private end(trigger: TipTrigger, anchor?: TipAnchor): void {
    const next = this.live.filter((entry) => entry.trigger !== trigger || (anchor !== undefined && !sameAnchor(entry.anchor, anchor)));
    if (next.length !== this.live.length) {
      this.live = next;
      this.publish();
    }
  }

  /**
   * Tells the listeners that the words of the tooltip shown for an anchor have changed, so it is drawn again; nothing
   * when that anchor is not the one shown.
   *
   * @param anchor The anchor whose words changed.
   */
  public refresh(anchor: TipAnchor): void {
    if (this.shown !== null && sameAnchor(this.shown.anchor, anchor)) {
      this.shown = { ...this.shown };
      for (const listener of this.listeners) {
        listener();
      }
    }
  }

  /**
   * Shows the newest live trigger's anchor, pinned when that trigger is a pin, telling the listeners when it changed.
   */
  private publish(): void {
    const top = this.live[this.live.length - 1];
    const pinned = top?.trigger === "pin";
    if (sameAnchor(top?.anchor ?? null, this.shown?.anchor ?? null) && pinned === (this.shown?.pinned ?? false)) {
      return;
    }
    this.shown = top === undefined ? null : { anchor: top.anchor, pinned };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
